import * as React from "react"

import { ChartColumnBig, Table2 } from "lucide-react"

import { cn } from "../../cn"
import { Button } from "../Button/Button"
import {
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableHead,
  ListTableHeader,
  ListTableRow,
} from "../ListTable/ListTable"
import { Popover, PopoverContent, PopoverTrigger } from "../Popover/Popover"

interface ChartTableColumn {
  key: string
  header: React.ReactNode
  /** Right-aligns the column, for numbers read down the column. */
  numeric?: boolean
}

interface ChartTableRow {
  key: string
  cells: Record<string, React.ReactNode>
}

/** The chart's data as rows and columns, shown in place of the chart on request. */
export interface ChartTable {
  columns: ChartTableColumn[]
  rows: ChartTableRow[]
}

export interface ChartFigureProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "children" | "title" | "aria-label" | "aria-labelledby" | "aria-describedby"
> {
  /** The chart's accessible name. Name what is plotted, not the chart type. */
  label: string
  /**
   * A sentence or two a screen reader reads after the name: the takeaway a
   * sighted reader gets from a glance, such as the range, the peak, or the
   * share above a threshold.
   */
  summary?: string
  /** When set, a toggle swaps the chart for this table and back. */
  table?: ChartTable
  /**
   * Where the table shows. `inline` (the default) swaps it in at the chart's
   * size; `popover` opens it beside the toggle and leaves the chart in place,
   * for charts too small to hold a readable table, such as a sparkline.
   */
  tableDisplay?: "inline" | "popover"
  /** The chart itself. */
  children: React.ReactNode
}

function ChartDataTable({
  label,
  table,
}: {
  label: string
  table: ChartTable
}) {
  return (
    <ListTable flush aria-label={label}>
      <ListTableHeader>
        <ListTableRow>
          {table.columns.map((column) => (
            <ListTableHead
              key={column.key}
              scope="col"
              className={cn(column.numeric && "text-right")}
            >
              {column.header}
            </ListTableHead>
          ))}
        </ListTableRow>
      </ListTableHeader>
      <ListTableBody>
        {table.rows.map((row) => (
          <ListTableRow key={row.key}>
            {table.columns.map((column) => (
              <ListTableCell
                key={column.key}
                className={cn(column.numeric && "text-right tabular-nums")}
              >
                {row.cells[column.key]}
              </ListTableCell>
            ))}
          </ListTableRow>
        ))}
      </ListTableBody>
    </ListTable>
  )
}

/**
 * Wraps a chart in a named figure with a text summary and an optional data
 * table view, so the chart's content is available without seeing it. The
 * figure keeps the size its `className` gives it in both views; a table
 * taller than the chart scrolls inside a bordered box.
 */
const ChartFigure = React.forwardRef<HTMLElement, ChartFigureProps>(
  (
    {
      label,
      summary,
      table,
      tableDisplay = "inline",
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const summaryId = React.useId()
    const [showTable, setShowTable] = React.useState(false)
    const popover = tableDisplay === "popover"
    const tableView = showTable && table !== undefined && !popover
    const revealClassName =
      "pointer-events-none opacity-0 focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/chart:pointer-events-auto group-hover/chart:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100"

    return (
      <figure
        {...props}
        ref={ref}
        aria-label={label}
        aria-describedby={summary ? summaryId : undefined}
        className={cn("group/chart relative m-0", className)}
      >
        {summary && (
          <p id={summaryId} className="sr-only">
            {summary}
          </p>
        )}
        {table && popover && (
          <Popover open={showTable} onOpenChange={setShowTable}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                icon
                tooltip="View as table"
                className={cn(
                  "absolute right-0 top-1/2 z-10 -translate-y-1/2 bg-surface-default",
                  !showTable && revealClassName,
                )}
              >
                <Table2 aria-hidden />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              aria-label={label}
              align="end"
              collisionPadding={8}
              className="max-h-72 w-max max-w-80 overflow-auto p-0.5"
            >
              <ChartDataTable label={label} table={table} />
            </PopoverContent>
          </Popover>
        )}
        {table && !popover && (
          <Button
            type="button"
            variant="ghost"
            icon
            tooltip={tableView ? "View as chart" : "View as table"}
            onClick={() => setShowTable((current) => !current)}
            className={cn(
              "absolute right-0 top-0 z-10 bg-surface-default",
              !tableView && revealClassName,
            )}
          >
            {tableView ? (
              <ChartColumnBig aria-hidden />
            ) : (
              <Table2 aria-hidden />
            )}
          </Button>
        )}
        {tableView ? (
          <div className="flex size-full flex-col pt-8">
            <div className="min-h-0 flex-1 overflow-auto rounded-md border border-coval-border bg-surface-default">
              <ChartDataTable label={label} table={table} />
            </div>
          </div>
        ) : (
          children
        )}
      </figure>
    )
  },
)

ChartFigure.displayName = "ChartFigure"

export { ChartFigure }
